import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  alpha,
  Stack,
  useTheme,
} from '@mui/material';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import PersonPinRoundedIcon from '@mui/icons-material/PersonPinRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { usePermission } from '../../hooks/usePermission';
import { useAuth } from '../../hooks/useAuth';
import { Role } from '../../types';

export const DRAWER_WIDTH = 260;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles: Role[];
}

interface NavSection {
  heading: string;
  roles: Role[];
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    heading: 'Platform',
    roles: [Role.PLATFORM_SUPER_ADMIN],
    items: [
      { label: 'Dashboard',  path: '/admin/dashboard', icon: <DashboardRoundedIcon fontSize="small" />, roles: [Role.PLATFORM_SUPER_ADMIN] },
      { label: 'Tenants',    path: '/admin/tenants',   icon: <BusinessRoundedIcon fontSize="small" />, roles: [Role.PLATFORM_SUPER_ADMIN] },
      { label: 'Plans',      path: '/admin/plans',     icon: <StarRoundedIcon fontSize="small" />,     roles: [Role.PLATFORM_SUPER_ADMIN] },
    ],
  },
  {
    heading: 'Management',
    roles: [Role.TENANT_ADMIN],
    items: [
      { label: 'Dashboard',        path: '/tenant/dashboard',  icon: <DashboardRoundedIcon fontSize="small" />,   roles: [Role.TENANT_ADMIN] },
      { label: 'Employees',        path: '/tenant/employees',  icon: <PeopleRoundedIcon fontSize="small" />,      roles: [Role.TENANT_ADMIN] },
      { label: 'Customers',        path: '/tenant/customers',  icon: <PersonPinRoundedIcon fontSize="small" />,   roles: [Role.TENANT_ADMIN] },
      { label: 'Geofences',        path: '/tenant/geofences',  icon: <LocationOnRoundedIcon fontSize="small" />,  roles: [Role.TENANT_ADMIN] },
      { label: 'Visit Assignments',path: '/tenant/visits',     icon: <AssignmentRoundedIcon fontSize="small" />,  roles: [Role.TENANT_ADMIN] },
      { label: 'Reports',          path: '/tenant/reports',    icon: <BarChartRoundedIcon fontSize="small" />,    roles: [Role.TENANT_ADMIN] },
    ],
  },
  {
    heading: 'Field',
    roles: [Role.MANAGER],
    items: [
      { label: 'Dashboard',       path: '/manager/dashboard', icon: <DashboardRoundedIcon fontSize="small" />,     roles: [Role.MANAGER] },
      { label: 'My Team',         path: '/manager/team',      icon: <GroupsRoundedIcon fontSize="small" />,        roles: [Role.MANAGER] },
      { label: 'Live Map',        path: '/manager/map',       icon: <MapRoundedIcon fontSize="small" />,           roles: [Role.MANAGER] },
      { label: 'Visits',          path: '/manager/visits',    icon: <TrackChangesRoundedIcon fontSize="small" />,  roles: [Role.MANAGER] },
      { label: 'Tracking Alerts', path: '/manager/alerts',    icon: <NotificationsRoundedIcon fontSize="small" />, roles: [Role.MANAGER] },
    ],
  },
];

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const navigate   = useNavigate();
  const location   = useLocation();
  const { user }   = usePermission();
  const { logout } = useAuth();
  const theme = useTheme();
  const sb = theme.palette.sidebar;
  const primary = theme.palette.primary.main;

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const handleNav = (path: string) => {
    navigate(path);
    onMobileClose?.();
  };

  const userInitials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) ?? '?';

  const roleBadge: Record<string, string> = {
    PLATFORM_SUPER_ADMIN: 'Super Admin',
    TENANT_ADMIN: 'Tenant Admin',
    MANAGER: 'Manager',
    EMPLOYEE: 'Employee',
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: sb.background,
        color: sb.text,
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.1)', borderRadius: 2 },
      }}
    >
      {/* ── Logo ─────────────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${sb.border}`,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '10px',
              background: `linear-gradient(135deg, ${primary} 0%, ${theme.palette.primary.dark} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${alpha(primary, 0.5)}`,
              flexShrink: 0,
            }}
          >
            <TrackChangesRoundedIcon sx={{ color: '#fff', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              sx={{ color: '#fff', fontWeight: 800, fontSize: '0.9rem', lineHeight: 1.2, letterSpacing: '-0.01em' }}
            >
              FieldForce
            </Typography>
            <Typography sx={{ color: sb.textMuted, fontSize: '0.65rem', letterSpacing: '0.04em' }}>
              Management Platform
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* ── Nav sections ────────────────────────────────── */}
      <Box sx={{ flex: 1, py: 1.5 }}>
        {SECTIONS.map((section) => {
          if (!user || !section.roles.includes(user.role)) return null;
          const visibleItems = section.items.filter((item) => item.roles.includes(user.role));
          if (!visibleItems.length) return null;

          return (
            <Box key={section.heading} sx={{ mb: 0.5 }}>
              <Typography
                sx={{
                  px: 3,
                  py: 1,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: sb.textMuted,
                }}
              >
                {section.heading}
              </Typography>
              <List disablePadding sx={{ px: 1.5 }}>
                {visibleItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNav(item.path)}
                        aria-current={active ? 'page' : undefined}
                        sx={{
                          borderRadius: '10px',
                          py: '8px',
                          px: '12px',
                          color: active ? sb.activeText : sb.text,
                          backgroundColor: active ? sb.activeBackground : 'transparent',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            bgcolor: active ? alpha(primary, 0.22) : sb.hoverBackground,
                            color: active ? sb.activeText : '#CBD5E1',
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 32,
                            color: active ? primary : sb.textMuted,
                            '& .MuiSvgIcon-root': {
                              filter: active ? 'drop-shadow(0 0 4px rgba(96,165,250,0.5))' : 'none',
                            },
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: active ? 700 : 500,
                            letterSpacing: '-0.005em',
                          }}
                        />
                        {active && (
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: primary,
                              boxShadow: `0 0 6px ${alpha(primary, 0.8)}`,
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>

      {/* ── User profile footer ─────────────────────────── */}
      <Box
        sx={{
          borderTop: `1px solid ${sb.border}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              p: 1.5,
              borderRadius: '12px',
              bgcolor: sb.surface,
              border: `1px solid ${sb.border}`,
            }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                background: `linear-gradient(135deg, ${primary} 0%, ${theme.palette.primary.dark} 100%)`,
                fontSize: '0.75rem',
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {userInitials}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: '#E2E8F0',
                fontSize: '0.8125rem',
                fontWeight: 600,
                lineHeight: 1.3,
              }}
            >
              {user?.name?.split(' ')[0] ?? 'User'}
            </Typography>
              <Typography sx={{ color: sb.textMuted, fontSize: '0.7rem', lineHeight: 1.2 }}>
                {roleBadge[user?.role ?? ''] ?? user?.role}
              </Typography>
            </Box>
            <Box
              component="button"
              onClick={async () => { await logout(); navigate('/login'); }}
              title="Sign out"
              aria-label="Sign out"
              sx={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: sb.textMuted,
                display: 'flex',
                alignItems: 'center',
                p: 0.5,
                borderRadius: '8px',
                transition: 'color 0.15s',
                flexShrink: 0,
                minWidth: 36,
                minHeight: 36,
                justifyContent: 'center',
                '&:hover': { color: theme.palette.error.main },
              }}
            >
              <LogoutRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
            position: 'relative',
            height: '100vh',
            boxShadow: '2px 0 16px rgba(0,0,0,0.12)',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
